import { z } from 'zod'

import type {
  CommonResponseCollectionSchoolAndRegionResponse,
  CommonResponseSliceSchoolWithRegionResponse,
  DownloadSchoolTestPdfData,
  GetRecentlyDownloadedSchoolsData,
  GetSchoolTestPageWithAllSchoolData,
  GetSchoolTestsGroupBySchoolData,
  SchoolTestPdfDownloadStatusResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { mapSliceContent, type Slice, sliceSchema } from '~/schema/slice'

import {
  type RecentlyDownloadedSchool,
  RecentlyDownloadedSchoolSchema,
  type School,
  SchoolSchema,
  type SchoolTest,
  type SchoolTestFile,
  SchoolTestFileSchema,
  type SchoolTestPdfQuota,
  SchoolTestPdfQuotaSchema,
  type SchoolTestsBySido,
  SchoolTestsBySidoSchema,
  SchoolTestSchema,
  type SchoolTestSchoolsBySido,
  SchoolTestSchoolsBySidoSchema,
} from './schemas'

const ENTITY = 'SchoolTest'

// 생성 타입은 두 응답의 중첩 클래스 School(SchoolAndRegionResponse.ChildRegion.School·
// SchoolTestGroupBySchoolResponse.School)을 이름이 같은 교육과정 School 로 덮어썼다. 학교 행의 code·name·
// 기출 목록이 스펙에 없다. 2026-10-07 백엔드 develop 의 schoolTest/controller/response 두 파일로 확인.
const asWireSchools = (raw: unknown): unknown => raw

export const adaptSchoolTestSchoolsBySido = (
  raw: CommonResponseCollectionSchoolAndRegionResponse['data'],
): SchoolTestSchoolsBySido[] =>
  parseApiResponse(z.array(SchoolTestSchoolsBySidoSchema), asWireSchools(raw), ENTITY)

export const adaptSchoolTestsBySido = (raw: GetSchoolTestsGroupBySchoolData): SchoolTestsBySido[] =>
  parseApiResponse(z.array(SchoolTestsBySidoSchema), asWireSchools(raw), ENTITY)

// 스펙은 yearMonth 를 java.time.YearMonth 객체로 그리지만 Spring Boot 의 Jackson 기본값이 날짜를 ISO 문자열로
// 쓰므로 "2026-10" 이 온다. 2026-10-07 백엔드 develop 의 schoolTest/controller/response/
// SchoolTestPdfDownloadStatusResponse.kt 와 legacy 손 선언(`${number}-${number}`)으로 확인.
const asWirePdfQuota = (raw: SchoolTestPdfDownloadStatusResponse): unknown => raw

export const adaptSchoolTestPdfQuota = (
  raw: SchoolTestPdfDownloadStatusResponse,
): SchoolTestPdfQuota => parseApiResponse(SchoolTestPdfQuotaSchema, asWirePdfQuota(raw), ENTITY)

// 회차의 semester(고등)·schoolTestWorkbook(원본 미수급) 은 서버가 `?` 로 선언하고 null 을 싣는데 스펙은 optional 로만
// 그린다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의 schoolTest/controller/response/SchoolTestV2Response.kt 로 확인.
const asWireSchoolTests = (raw: GetSchoolTestPageWithAllSchoolData): unknown => raw

export const adaptSchoolTests = (raw: GetSchoolTestPageWithAllSchoolData): Slice<SchoolTest> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(SchoolTestSchema), asWireSchoolTests(raw), ENTITY),
    (schoolTest) => schoolTest,
  )

export const adaptSchools = (
  raw: CommonResponseSliceSchoolWithRegionResponse['data'],
): Slice<School> =>
  mapSliceContent(parseApiResponse(sliceSchema(SchoolSchema), raw, ENTITY), (school) => school)

export const adaptSchoolTestFile = (raw: DownloadSchoolTestPdfData): SchoolTestFile =>
  parseApiResponse(SchoolTestFileSchema, raw, ENTITY)

export const adaptRecentlyDownloadedSchools = (
  raw: GetRecentlyDownloadedSchoolsData,
): RecentlyDownloadedSchool[] =>
  parseApiResponse(z.object({ schools: z.array(RecentlyDownloadedSchoolSchema) }), raw, ENTITY)
    .schools
